import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getConcept, getTrainingCapabilities } from './api';
import ProductionWorkspace from './ProductionWorkspace';
import SchedulingPanel from './SchedulingPanel';
import StageTrail from './StageTrail';
import ConceptStage from './stages/ConceptStage';
import SourcesStage from './stages/SourcesStage';
import MatchStage from './stages/MatchStage';
import ReviewStage from './stages/ReviewStage';
import PricingStage from './stages/PricingStage';
import ProductionStage from './stages/ProductionStage';

// The concept workspace, as a stepper.
//
// It used to be a permanent editorial column beside a single inspector scroll of ten
// sections, all rendered at every stage: pricing controls on a draft, routing on a sold
// class, eight competing actions and nothing saying which was next. The concept text held
// half the screen while someone worked six sections away from it.
//
// So the work is staged. Nothing that was rendered before was dropped in the move - the
// stages carry every field and control, and stage 1 additionally shows several fields the
// payload always had but the page never displayed. Each stage also renders its own activity
// trail, because every mutating action now records an event tagged with the stage that did it.
//
// Stages are always reachable: an author may read ahead or back freely. Reachability is
// navigation, and it is separate from whether a stage's actions are permitted, which the
// gates and roles still decide on the server.
const STAGES = [
  { id: 'concept',    n: 1, label: 'Concept',        hint: 'What this class is' },
  { id: 'sources',    n: 2, label: 'Sources',        hint: 'What backs it up' },
  { id: 'match',      n: 3, label: 'Match & target', hint: 'Where it gets filed' },
  { id: 'review',     n: 4, label: 'Review',         hint: 'Who signs off' },
  { id: 'pricing',    n: 5, label: 'Pricing & sale', hint: 'What it costs' },
  { id: 'production', n: 6, label: 'Production',     hint: 'Building it' },
  { id: 'scheduling', n: 7, label: 'Scheduling',     hint: 'When it runs', liveOnly: true },
];

// A tick means "this stage has nothing outstanding", read from state the server already
// reports. Deliberately not a stored per-stage flag: a second source of truth about
// progress is exactly the kind of thing that drifts.
const stageDone = (id, c) => {
  switch (id) {
    case 'concept': return (c.objectives || []).length > 0 && (c.needs_revision_reasons || []).length === 0;
    case 'sources': return !c.source_review?.blocked;
    case 'match': return Boolean(c.class_target?.confirmed);
    // EVERY role on the review path, not "any approval and nothing pending". A path of
    // director + curriculum developer + LXD is routed sequentially, so between two routes
    // there is one approval and no pending review - which the old test read as complete and
    // ticked green while two roles had not seen it.
    case 'review': return (c.review_path || ['director']).every((role) =>
      (c.reviews || []).filter((r) => r.review_type === role).slice(-1)[0]?.decision === 'approved')
      && (c.pending_reviews || []).length === 0;
    case 'pricing': return Boolean(c.pricing?.director_approved_price)
      && !(c.pricing?.pricing_exception && !c.pricing?.pricing_exception_cleared);
    case 'production': return (c.production?.artifacts || []).length > 0;
    case 'scheduling': return c.status === 'approved_for_scheduling';
    default: return false;
  }
};

const LOCKED_STATUSES = ['approved_for_client_use', 'approved_for_sale', 'recognized_sale',
  'approved_for_development', 'approved_for_publication', 'approved_for_scheduling'];

export default function ConceptWorkspace({ conceptId, onBack }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['training-concept', conceptId], queryFn: () => getConcept(conceptId),
  });
  const c = data?.concept;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['training-concept', conceptId] });

  const { data: caps } = useQuery({ queryKey: ['training-me'], queryFn: getTrainingCapabilities });
  const isDirector = Boolean(caps?.is_director);

  const [stage, setStage] = React.useState('concept');
  const [showProduction, setShowProduction] = React.useState(false);
  const [openArtifactId, setOpenArtifactId] = React.useState(null);
  const [openAddArtifact, setOpenAddArtifact] = React.useState(false);
  const enterProduction = ({ artifactId = null, add = false } = {}) => {
    setOpenArtifactId(artifactId);
    setOpenAddArtifact(add);
    setShowProduction(true);
  };

  if (isLoading) return <div className="text-sm text-gray-500">Loading concept…</div>;
  if (isError) return (
    <div>
      <button onClick={onBack} className="text-xs text-gray-500 mb-3">← All concepts</button>
      <div className="text-sm text-red-500">
        Could not load this concept{error?.message ? `: ${error.message}` : ''}.
      </div>
    </div>
  );
  if (!c) return <div className="text-sm text-gray-500">Loading…</div>;

  if (showProduction) return (
    <ProductionWorkspace conceptId={conceptId} initialArtifactId={openArtifactId}
      initialAddOpen={openAddArtifact} onBack={() => setShowProduction(false)} />
  );

  // Server-decided production availability. Defaults closed so a payload from an older API
  // renders an explanation rather than silently offering production it would refuse.
  const gate = c.production_gate || {
    can_approve_development: false, can_open_workspace: Boolean(c.production),
    approver_permitted: false, class_target_confirmed: false,
    development_pending_class_target: false, blocked_reason: null,
  };
  const next = c.next_action;
  const live = c.delivery_recommendation === 'live';
  const stages = STAGES.filter((s) => !s.liveOnly || live);
  const active = stages.find((s) => s.id === stage) || stages[0];
  const locked = LOCKED_STATUSES.includes(c.status);

  const idx = stages.findIndex((s) => s.id === active.id);
  const prev = idx > 0 ? stages[idx - 1] : null;
  const nxt = idx < stages.length - 1 ? stages[idx + 1] : null;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <button onClick={onBack} className="text-xs text-gray-500">← All concepts</button>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1.5 font-semibold px-3 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800">
            {String(c.status).replace(/_/g, ' ')}
          </span>
          <span className="text-gray-500">
            {c.client ? c.client.name : <span className="text-gray-400">no client attached</span>}
          </span>
        </div>
      </div>

      {/* One next action, named by the server from the same rules the endpoints enforce,
          with a jump to the stage that performs it. This replaces inferring the pipeline
          from which of eight buttons happened to be enabled. */}
      {next && (
        <div className={`rounded-2xl px-4 py-3 mb-3 border ${next.permitted
          ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
          : 'bg-gray-50 border-gray-200 dark:bg-gray-800/40 dark:border-gray-700'}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Next</div>
              <div className="text-sm font-semibold">{next.label}</div>
              {next.reason && <div className="text-[11px] text-gray-500 mt-0.5 max-w-2xl">{next.reason}</div>}
            </div>
            {next.stage && next.permitted && next.stage !== active.id && (
              <button onClick={() => setStage(next.stage)}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#1f6feb] text-white shrink-0">
                Go to {stages.find((s) => s.id === next.stage)?.label || next.stage} →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stage nav. Every stage is reachable at any time; the tick means "nothing
          outstanding here", not "you may not go back". */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {stages.map((s) => {
          const done = stageDone(s.id, c);
          const isActive = s.id === active.id;
          const isNext = next?.stage === s.id && next?.permitted;
          return (
            <button key={s.id} onClick={() => setStage(s.id)} title={s.hint}
              className={`shrink-0 text-left px-3 py-2 rounded-xl border transition ${isActive
                ? 'bg-white border-blue-400 shadow-sm dark:bg-gray-800 dark:border-blue-500'
                : 'bg-gray-50 border-gray-200 hover:bg-white dark:bg-gray-800/40 dark:border-gray-700'}`}>
              <div className="flex items-center gap-1.5">
                <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold ${
                  done ? 'bg-green-500 text-white'
                    : isActive ? 'bg-blue-500 text-white'
                    : 'bg-gray-300 text-white dark:bg-gray-600'}`}>
                  {done ? '✓' : s.n}
                </span>
                <span className={`text-[11px] font-semibold ${isActive ? '' : 'text-gray-500'}`}>{s.label}</span>
                {isNext && !isActive && (
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="The next action is in this stage" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Active stage. The concept read is wide and typeset; the working stages sit in the
          studio panel so their controls keep the density they were designed at. */}
      {active.id === 'concept' ? (
        <div>
          <ConceptStage concept={c} />
          {/* Only when there is a trail to show. The panel used to render regardless, so a
              concept with no concept-stage events got an empty dark box under a typeset
              card. */}
          {(c.events || []).some((e) => e.stage === 'concept') && (
            <div className="rounded-2xl bg-[#0e1117] text-[#e6edf6] p-4 mt-3">
              <StageTrail events={c.events} stage="concept" />
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl bg-[#0e1117] text-[#e6edf6] p-5">
          <div className="mb-4">
            <div className="text-sm font-semibold">{active.label}</div>
            <div className="text-[11px] text-[#7d8aa0]">{active.hint}</div>
          </div>

          {active.id === 'sources' && (
            <SourcesStage concept={c} conceptId={conceptId} isDirector={isDirector} onChanged={invalidate} />
          )}
          {active.id === 'match' && (
            <MatchStage concept={c} conceptId={conceptId} onChanged={invalidate} locked={locked} />
          )}
          {active.id === 'review' && (
            <ReviewStage concept={c} conceptId={conceptId} onChanged={invalidate} />
          )}
          {active.id === 'pricing' && (
            <PricingStage concept={c} conceptId={conceptId} isDirector={isDirector} onChanged={invalidate} />
          )}
          {active.id === 'production' && (
            <ProductionStage concept={c} conceptId={conceptId} gate={gate}
              onChanged={invalidate} onEnterProduction={enterProduction}
              onGoToStage={setStage} />
          )}
          {active.id === 'scheduling' && (
            live && c.status === 'approved_for_scheduling'
              ? <SchedulingPanel conceptId={c.id} />
              : <div className="text-[11px] text-[#7d8aa0]">
                  Scheduling opens once this live class is approved for scheduling.
                </div>
          )}

          <StageTrail events={c.events} stage={active.id} />
        </div>
      )}

      {/* Stepper navigation, so the rail reads as a sequence rather than seven tabs. */}
      <div className="flex items-center justify-between mt-3">
        <button onClick={() => prev && setStage(prev.id)} disabled={!prev}
          className="text-xs text-gray-500 disabled:opacity-0">← {prev?.label}</button>
        <button onClick={() => nxt && setStage(nxt.id)} disabled={!nxt}
          className="text-xs text-blue-600 dark:text-blue-400 disabled:opacity-0">{nxt?.label} →</button>
      </div>
    </div>
  );
}
